import {
  Box,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Loader } from '@/components/layout/loading';
import { NetworkInterfaceSelect } from '@/components/selectors/networkInterfaceSelect';
import { ResourceGroupSelect } from '@/components/selectors/resourceGroupSelect';
import { RestoreRegionSelect } from '@/components/selectors/restoreRegionSelect';
import { SELECT_CREATE_NEW } from '@/components/shared/autocompleteWithNew';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { NetworkInterfaceConfig } from './networkInterfaceConfig';
import { StartInstanceAfterRestoreToggle } from './startInstanceAfterRestoreToggle';
import { VirtualMachineSizeSection } from './virtualMachineSizeSection';

import { StepContainer } from '../../wizard/StepContainer';
import type { RestoreAzureVMState } from '../restoreAzureVMWizard';

export const useValidateNetwork = (
  virtualNetworkName?: string,
  newVirtualNetworkName?: string,
  subnetName?: string,
  newSubnetName?: string,
  networkInterface?: string
) => {
  return useMemo(() => {
    if (!networkInterface) {
      return false;
    }
    if (networkInterface === SELECT_CREATE_NEW) {
      if (!virtualNetworkName || !subnetName) {
        return false;
      }
      if (virtualNetworkName === SELECT_CREATE_NEW) {
        if (!newVirtualNetworkName || subnetName !== SELECT_CREATE_NEW) {
          return false;
        }
      }
      if (subnetName === SELECT_CREATE_NEW) {
        if (!newSubnetName) {
          return false;
        }
      }
      return !!virtualNetworkName && !!subnetName;
    }
    return true;
  }, [
    virtualNetworkName,
    newVirtualNetworkName,
    subnetName,
    newSubnetName,
    networkInterface,
  ]);
};

export const AzureVmConfiguration = (props: StepProps<RestoreAzureVMState>) => {
  const [isNameValid, setIsNameValid] = useState(false);
  const [isOriginalVmCompatible, setIsOriginalVmCompatible] = useState<
    boolean | undefined
  >(undefined);
  const setVMName = (vmName: string, isValid: boolean) => {
    props.setWizardState((state) => ({ ...state, vmName }));
    setIsNameValid(isValid);
  };
  const onKeepOriginalTagsChange = (keepOriginalTags: boolean) =>
    props.setWizardState((state) => ({ ...state, keepOriginalTags }));
  const onTagsChange = (tags: Record<string, string>) =>
    props.setWizardState((state) => ({ ...state, tags }));

  const onCompatibilityChange = useCallback(
    (compatible: boolean | undefined) => {
      setIsOriginalVmCompatible(compatible);
    },
    []
  );

  const onNextClick = async () => {
    if (isOriginalVmCompatible === false) {
      props.setWizardState((state) => ({
        ...state,
        keepOriginalVMSize: false,
      }));
    }
    props.setNextStep(props.currentStep.next?.[0]);
  };

  const validNetwork = useValidateNetwork(
    props.wizardState.virtualNetworkName,
    props.wizardState.newVirtualNetworkName,
    props.wizardState.subnetName,
    props.wizardState.newSubnetName,
    props.wizardState.networkInterface
  );

  return (
    <StepContainer
      sx={{ padding: '0' }}
      stepperLabels={props.stepperLabels}
      stepperIndex={1}
      onBackClick={props.back}
      canGoNext={() =>
        isNameValid &&
        !!props.wizardState.vmName &&
        !!props.wizardState.resourceGroup &&
        !!props.wizardState.location &&
        validNetwork &&
        (isOriginalVmCompatible === false
          ? Boolean(props.wizardState.vmSize)
          : props.wizardState.keepOriginalVMSize ||
            Boolean(props.wizardState.vmSize)) &&
        isOriginalVmCompatible !== undefined
      }
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
        />
      }
    >
      <Box className='mx-[40px] my-[24px]'>
        <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
          <VmName
            cloudAccountId={props.wizardState.restoreAccount?.id}
            resourceGroup={props.wizardState.resourceGroup}
            vmName={props.wizardState.vmName ?? ''}
            onChange={setVMName}
          />
          <RestoreRegionSelect
            isRequired
            restoreAccountId={props.wizardState.restoreAccount?.id}
            cloudProvider={Provider.Azure}
            regionName={props.wizardState.location}
            onChange={(location) =>
              props.setWizardState((state) => ({ ...state, location }))
            }
          />
        </Stack>
        <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
          <ResourceGroupSelect
            accountId={props.wizardState.restoreAccount?.id}
            resourceGroup={props.wizardState.resourceGroup}
            onChange={(resourceGroup) =>
              props.setWizardState((state) => ({
                ...state,
                resourceGroup,
              }))
            }
          />
          <NetworkInterfaceSelect
            accountId={props.wizardState.restoreAccount?.id}
            resourceGroup={props.wizardState.resourceGroup}
            networkInterface={props.wizardState.networkInterface}
            location={props.wizardState.location}
            onChange={(networkInterface) =>
              props.setWizardState((state) => ({
                ...state,
                networkInterface,
              }))
            }
          />
        </Stack>
      </Box>
      <Divider />
      {props.wizardState.networkInterface === SELECT_CREATE_NEW &&
        props.wizardState.restoreAccount?.id &&
        props.wizardState.resourceGroup && (
          <>
            <CardContent className='p-[40px]'>
              <Stack gap='40px'>
                <Typography>Network Interface configuration</Typography>
                <NetworkInterfaceConfig
                  accountId={props.wizardState.restoreAccount?.id}
                  resourceGroup={props.wizardState.resourceGroup}
                  location={props.wizardState.location}
                  virtualNetworkName={props.wizardState.virtualNetworkName}
                  setVirtualNetworkName={(virtualNetworkName) =>
                    props.setWizardState((state) => ({
                      ...state,
                      virtualNetworkName,
                    }))
                  }
                  newVirtualNetworkName={
                    props.wizardState.newVirtualNetworkName
                  }
                  setNewVirtualNetworkName={(newVirtualNetworkName) =>
                    props.setWizardState((state) => ({
                      ...state,
                      newVirtualNetworkName,
                    }))
                  }
                  subnetName={props.wizardState.subnetName}
                  setSubnetName={(subnetName) =>
                    props.setWizardState((state) => ({ ...state, subnetName }))
                  }
                  newSubnetName={props.wizardState.newSubnetName}
                  setNewSubnetName={(newSubnetName) =>
                    props.setWizardState((state) => ({
                      ...state,
                      newSubnetName,
                    }))
                  }
                />
              </Stack>
            </CardContent>
            <Divider />
          </>
        )}
      <CardContent className='w-full px-[40px]'>
        <VirtualMachineSizeSection
          accountId={props.wizardState.restoreAccount?.id}
          location={props.wizardState.location}
          originalVMSize={props.wizardState.originalVMSize}
          vmSize={props.wizardState.vmSize}
          keepOriginalVMSize={props.wizardState.keepOriginalVMSize}
          disksState={props.wizardState.disksState}
          onVmSizeChange={(vmSize) =>
            props.setWizardState((state) => ({ ...state, vmSize }))
          }
          onKeepOriginalVMSizeChange={(keepOriginalVMSize) =>
            props.setWizardState((state) => ({
              ...state,
              keepOriginalVMSize,
            }))
          }
          onCompatibilityChange={onCompatibilityChange}
        />
      </CardContent>
      <Divider />
      <CardContent className='px-0'>
        <TagsSection
          initialTags={props.wizardState.initialTags}
          tags={props.wizardState.tags}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          setKeepOriginalTags={onKeepOriginalTagsChange}
          onChange={onTagsChange}
        />
      </CardContent>
      <Divider />
      <StartInstanceAfterRestoreToggle
        checked={props.wizardState.startInstanceAfterRestore}
        onChange={() => {
          props.setWizardState((state) => ({
            ...state,
            startInstanceAfterRestore: !state.startInstanceAfterRestore,
          }));
        }}
      />
    </StepContainer>
  );
};

const VmName = ({
  cloudAccountId,
  resourceGroup,
  vmName,
  onChange,
}: {
  cloudAccountId?: string;
  resourceGroup?: string;
  vmName: string;
  onChange: (value: string, isValid: boolean) => void;
}) => {
  const [value, setValue] = useState(vmName);
  const [error, setError] = useState('');
  const [debouncedValue, setDebouncedValue] = useState(vmName);
  const dal = useDAL();
  const { data: body, isLoading } = dal.restore.azureVm.useCheckNameExists(
    cloudAccountId,
    resourceGroup,
    debouncedValue
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, 200);

    return () => {
      clearTimeout(handler);
    };
  }, [value]);

  useEffect(() => {
    if (!isLoading && body?.exists) {
      setError('VM name already exists');
    } else {
      setError('');
    }
  }, [body, isLoading]);

  useEffect(() => {
    onChange(debouncedValue, !error && body?.exists === false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body, debouncedValue, error]);

  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>VM name</FormLabel>
      <TextField
        value={value}
        onChange={(e) => setValue(e.target.value)}
        error={Boolean(error)}
        helperText={error}
        inputProps={{
          maxLength: 80,
        }}
        InputProps={{
          endAdornment: isLoading ? (
            <InputAdornment position='end'>
              <Loader variant='inline' />
            </InputAdornment>
          ) : !value ? undefined : body ? (
            body.exists ? (
              <InputAdornment position='end'>
                <i className='clarity-error-line text-[22px]' />
              </InputAdornment>
            ) : (
              <InputAdornment position='end'>
                <i className='material-symbols-check-circle-outline-rounded' />
              </InputAdornment>
            )
          ) : undefined,
        }}
      />
    </FormControl>
  );
};
